import { requestAPI } from '../../utils/request';
import { ViewMode } from '../../hooks/viewMode';

export type IdentifierKey = 'userId' | 'userCode' | 'merchantReference';

const normalizeHeader = (s: string) =>
  String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, '_');

// Find the column index in the CSV header whose name matches one of the
// target's aliases. Returns -1 when the user picked a filter but the CSV
// doesn't have a matching column — caller surfaces the error.
export const findColumnForTarget = (
  header: string[] | null,
  target: IdTarget
): number => {
  if (!header) return -1;
  const aliases = new Set(target.csvHeaderAliases);
  return header.findIndex((h) => aliases.has(normalizeHeader(h)));
};

// Extracts the non-empty values from the column matching the selected
// filter. `colIndex` is -1 when no matching column exists.
export const extractColumn = (
  header: string[] | null,
  rows: string[][],
  target: IdTarget
): { colIndex: number; values: string[] } => {
  const colIndex = findColumnForTarget(header, target);
  if (colIndex < 0) return { colIndex: -1, values: [] };
  const values = rows
    .map((r) => String(r[colIndex] ?? '').trim())
    .filter(Boolean);
  return { colIndex, values };
};

export type IdTarget = {
  key: IdentifierKey;
  // i18n message id for the human label
  labelId: string;
  // first-column header names that auto-select this identifier
  csvHeaderAliases: string[];
};

export type ResolveResult = {
  matched: any[];
  unmatched: string[];
};

export type ExecuteResult = {
  succeeded: any[];
  failed: { record: any; error: string }[];
};

export type ProgressFn = (done: number, total: number) => void;

export type BulkActionConfig = {
  key: string;
  // i18n message ids
  labelId: string;
  descriptionId: string;
  viewMode: ViewMode;
  destructive: boolean;
  idTargets: IdTarget[];
  resolve: (values: string[], target: IdTarget) => Promise<ResolveResult>;
  execute: (records: any[], onProgress: ProgressFn) => Promise<ExecuteResult>;
};

// Identifiers sent per /fleets/users request (array query param). Keeps the
// URL well under server/proxy limits while still collapsing a whole CSV into
// a handful of requests instead of one-per-row.
const ID_CHUNK = 200;

const chunk = <T>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Normalises the bulk-archive `errors` payload into a plain id→message map.
// The backend serialises an empty Java Map as `{ entry: [] }` and a populated
// one as `{ entry: [{ key, value }, ...] }`, so we accept both that shape and
// the spec'd `{ [id]: message }` form.
const parseBulkErrors = (raw: any): Record<string, string> => {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  if (Array.isArray((raw as any).entry)) {
    for (const e of (raw as any).entry) {
      if (e && e.key != null) out[String(e.key)] = String(e.value ?? '');
    }
    return out;
  }
  for (const [k, v] of Object.entries(raw)) {
    if (typeof v === 'string') out[k] = v;
  }
  return out;
};

// query param name + the user field to key returned users by, per identifier
const TARGET_QUERY: Record<
  IdentifierKey,
  { param: string; field: (u: any) => string | undefined }
> = {
  userId: { param: 'id', field: (u) => u?.id },
  userCode: { param: 'referralCode', field: (u) => u?.referralCode },
  merchantReference: {
    param: 'merchantReference',
    field: (u) => u?.merchantUserReference,
  },
};

const archiveUsers: BulkActionConfig = {
  key: 'archiveUsers',
  labelId: 'fleetadmin.bulk.archive.title',
  descriptionId: 'fleetadmin.bulk.archive.desc',
  viewMode: ViewMode.USERS,
  destructive: true,
  idTargets: [
    {
      key: 'userId',
      labelId: 'fleetadmin.bulk.identifier.userId',
      csvHeaderAliases: ['user_id', 'userid', 'id', 'userId'],
    },
    {
      key: 'userCode',
      labelId: 'fleetadmin.bulk.identifier.userCode',
      csvHeaderAliases: [
        'user_code',
        'usercode',
        'referral_code',
        'referralcode',
        'code',
        'userCode',
      ],
    },
    {
      key: 'merchantReference',
      labelId: 'fleetadmin.bulk.identifier.merchantReference',
      csvHeaderAliases: [
        'merchantuserreference',
        'merchant_user_reference',
        'merchant_user_ref',
        'merchantreference',
        'merchant_reference',
      ],
    },
  ],
  // One request per chunk with the identifier passed as an array query param
  // (requestAPI serialises arrays as repeated params), instead of one request
  // per CSV row.
  resolve: async (values, target) => {
    const cleaned = Array.from(
      new Set(values.map((v) => String(v).trim()).filter(Boolean))
    );
    const { param, field } = TARGET_QUERY[target.key];

    const byKey = new Map<string, any>();
    for (const part of chunk(cleaned, ID_CHUNK)) {
      try {
        const data = await requestAPI('/fleets/users', {
          offset: '0',
          limit: String(part.length),
          [param]: part,
        });
        for (const u of data?.users ?? []) {
          const k = field(u);
          if (k != null) byKey.set(String(k), u);
        }
      } catch (e) {
        // a failed chunk just means those values stay unmatched
      }
    }

    const matched: any[] = [];
    const unmatched: string[] = [];
    for (const value of cleaned) {
      const user = byKey.get(value);
      if (user) matched.push(user);
      else unmatched.push(value);
    }
    return { matched, unmatched };
  },
  // Batch archive: one DELETE /fleets/users?id=…&id=… per chunk. The backend
  // processes each id independently and returns archived ids + a per-id error
  // map, which we map back onto the selected records.
  execute: async (records, onProgress) => {
    const succeeded: any[] = [];
    const failed: { record: any; error: string }[] = [];

    const byId = new Map<string, any>();
    for (const r of records) {
      if (r?.id != null) byId.set(String(r.id), r);
    }
    const ids = Array.from(byId.keys());
    const total = ids.length;
    let done = 0;

    for (const part of chunk(ids, ID_CHUNK)) {
      try {
        const data = await requestAPI(
          '/fleets/users',
          { id: part },
          { method: 'DELETE' }
        );

        const archivedIds: string[] = Array.isArray(data?.archivedIds)
          ? data.archivedIds
          : [];
        const errors = parseBulkErrors(data?.errors);

        const seen = new Set<string>();
        for (const id of archivedIds) {
          const key = String(id);
          seen.add(key);
          const record = byId.get(key);
          if (record) succeeded.push(record);
        }
        for (const [id, msg] of Object.entries(errors)) {
          const key = String(id);
          seen.add(key);
          failed.push({
            record: byId.get(key) ?? { id: key },
            error: msg || 'Could not be archived',
          });
        }
        for (const id of part) {
          if (!seen.has(id)) {
            failed.push({
              record: byId.get(id) ?? { id },
              error: 'No response from server',
            });
          }
        }
      } catch (e: any) {
        const message = e?.message || 'Could not be archived';
        for (const id of part) {
          failed.push({
            record: byId.get(id) ?? { id },
            error: message,
          });
        }
      }
      done += part.length;
      onProgress(done, total);
    }

    return { succeeded, failed };
  },
};

export const BULK_ACTIONS: BulkActionConfig[] = [archiveUsers];

export const getBulkActionsForViewMode = (viewMode: string) =>
  BULK_ACTIONS.filter((a) => a.viewMode === viewMode);

export const getBulkAction = (key: string) =>
  BULK_ACTIONS.find((a) => a.key === key);
